import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assessmentAPI } from '../services/api';
import { Assessment } from '../types';
import './InitialAssessment.css';

const InitialAssessment: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const subject = location.state?.subject || 'Biology';
  
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAssessments();
  }, [subject]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await assessmentAPI.getPublishedAssessments(subject);
      setAssessments(response.assessments);
      
      // Auto-select the first assessment if available
      if (response.assessments.length > 0) {
        setSelectedAssessment(response.assessments[0]);
      }
    } catch (err: any) {
      setError('Failed to load assessments');
      console.error('Error loading assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicToggle = (topic: string) => {
    setSelectedTopics(prev => 
      prev.includes(topic) 
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  const handleStartAssessment = () => {
    if (!selectedAssessment) {
      setError('Please select an assessment');
      return;
    }

    // Navigate to assessment taking page
    navigate('/take-assessment', {
      state: {
        assessment: selectedAssessment,
        selectedTopics: selectedTopics.length > 0 ? selectedTopics : selectedAssessment.topics,
        isInitial: true
      }
    });
  };

  if (loading) {
    return (
      <div className="initial-assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading available assessments...</p>
        </div>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="initial-assessment-container">
        <div className="no-assessments">
          <h2>No Assessments Available</h2>
          <p>There are currently no published assessments for {subject}.</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="initial-assessment-container">
      <header className="assessment-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          ← Back to Dashboard
        </button>
        <h1>Initial Assessment - {subject}</h1>
        <p>Welcome, {student?.first_name}! Take an initial assessment to help us understand your current knowledge level.</p>
      </header>

      <div className="assessment-content">
        {/* Assessment Selection */}
        <section className="assessment-selection">
          <h2>Available Assessments</h2>
          <div className="assessments-grid">
            {assessments.map((assessment) => (
              <div 
                key={assessment.id}
                className={`assessment-card ${selectedAssessment?.id === assessment.id ? 'selected' : ''}`}
                onClick={() => setSelectedAssessment(assessment)}
              >
                <h3>{assessment.title}</h3>
                <p className="assessment-description">{assessment.description}</p>
                <div className="assessment-meta">
                  <span className="question-count">{assessment.question_count} questions</span>
                  <span className="quality-score">Quality: {Math.round(assessment.quality_score * 100)}%</span>
                </div>
                <div className="topics-preview">
                  <strong>Topics covered:</strong> {assessment.topics.slice(0, 3).join(', ')}
                  {assessment.topics.length > 3 && ` +${assessment.topics.length - 3} more`}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Topic Selection */}
        {selectedAssessment && (
          <section className="topic-selection">
            <h2>Select Topics (Optional)</h2>
            <p>Choose specific topics you'd like to focus on, or leave all selected for a comprehensive assessment.</p>
            
            <div className="topics-grid">
              {selectedAssessment.topics.map((topic) => (
                <label key={topic} className="topic-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTopics.length === 0 || selectedTopics.includes(topic)}
                    onChange={() => handleTopicToggle(topic)}
                  />
                  <span className="checkmark"></span>
                  <span className="topic-name">{topic}</span>
                </label>
              ))}
            </div>

            <div className="topic-actions">
              <button 
                onClick={() => setSelectedTopics([])}
                className="select-all-button"
              >
                Select All Topics
              </button>
              <button 
                onClick={() => setSelectedTopics(selectedAssessment.topics)}
                className="clear-selection-button"
              >
                Clear Selection
              </button>
            </div>
          </section>
        )}

        {/* Assessment Summary */}
        {selectedAssessment && (
          <section className="assessment-summary">
            <h2>Assessment Summary</h2>
            <div className="summary-card">
              <div className="summary-item">
                <strong>Assessment:</strong> {selectedAssessment.title}
              </div>
              <div className="summary-item">
                <strong>Total Questions:</strong> {selectedAssessment.question_count}
              </div>
              <div className="summary-item">
                <strong>Selected Topics:</strong> {
                  selectedTopics.length === 0 
                    ? `All topics (${selectedAssessment.topics.length})`
                    : `${selectedTopics.length} of ${selectedAssessment.topics.length} topics`
                }
              </div>
              <div className="summary-item">
                <strong>Estimated Time:</strong> {Math.ceil(selectedAssessment.question_count * 1.5)} minutes
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              onClick={handleStartAssessment}
              className="start-assessment-button"
              disabled={!selectedAssessment}
            >
              Start Initial Assessment
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

export default InitialAssessment;